/**
 * Kreditlimit-Ampel: Limit, offene Forderungen und der freie Spielraum in
 * einer Zeile, eingefaerbt nach Ausschoepfung.
 *
 *   gruen  unter 80 % ausgeschoepft
 *   gelb   80 bis 100 %
 *   rot    ueberschritten - der Spielraum ist negativ
 *
 * Das Limit selbst bleibt fest; was sich bewegt, ist der offene Betrag
 * dagegen. Mit `pending` (Wert des gerade erfassten Belegs) zeigt die Zeile
 * zusaetzlich, was nach diesem Beleg noch frei waere.
 */

import { formatAmount } from '@erp/shared';

export function CreditGauge({
  creditLimit,
  openAmount,
  pending = 0,
}: {
  /** Kreditlimit in Cent. 0 = keine Anzeige. */
  creditLimit: number;
  /** Offene Forderungen in Cent. */
  openAmount: number;
  /** Bruttowert eines noch nicht festgeschriebenen Belegs, der dazukaeme. */
  pending?: number;
}) {
  if (creditLimit <= 0) return null;

  const free = creditLimit - openAmount;
  const ratio = openAmount / creditLimit;
  const level: 'ok' | 'warn' | 'over' = free < 0 ? 'over' : ratio >= 0.8 ? 'warn' : 'ok';
  const color =
    level === 'over' ? 'var(--danger)' : level === 'warn' ? 'var(--warning)' : 'var(--success)';
  const freeAfter = free - pending;

  return (
    <span
      className="row"
      style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
      title={`${Math.round(ratio * 100)} % des Kreditlimits ausgeschöpft`}
    >
      <span
        aria-hidden
        style={{
          display: 'inline-block',
          width: 10,
          height: 10,
          borderRadius: '50%',
          background: color,
          flex: '0 0 auto',
        }}
      />
      <span className="faint">Kreditlimit</span>
      <strong className="mono">{formatAmount(creditLimit)} €</strong>
      <span className="faint">offen</span>
      <strong className="mono">{formatAmount(openAmount)} €</strong>
      <span className="faint">{free < 0 ? 'überzogen um' : 'frei'}</span>
      <strong className="mono" style={{ color }}>
        {formatAmount(Math.abs(free))} €
      </strong>
      {pending > 0 ? (
        <span className="faint">
          nach diesem Beleg{' '}
          <strong className="mono" style={{ color: freeAfter < 0 ? 'var(--danger)' : undefined }}>
            {freeAfter < 0 ? `−${formatAmount(-freeAfter)}` : formatAmount(freeAfter)} €
          </strong>
        </span>
      ) : null}
    </span>
  );
}
